import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { Car } from '../../../../../models/car';
import { Telemetry } from '../../../../../models/telemetry';
import { ButtonModule } from 'primeng/button';
import { ShowLocationDialog } from '../../../../../@shared/dialogs/show-location-dialog/show-location-dialog';
import { CommonModule } from '@angular/common';
import { CapitalizePipe } from '../../../../../@pipes/capitalize-pipe';
import { TagModule } from 'primeng/tag';
import { RentalType } from '../../../../../models/enums';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CarService } from '../../../../../services/vehicles/car-service';
import { ChartModule } from 'primeng/chart';
import { GoogleMapsModule } from '@angular/google-maps';
import { GREEN_ZONES, RED_ZONES } from '../../../../../constants/zones.constants';

@Component({
  selector: 'app-car-details-general-info-component',
  imports: [CommonModule, CapitalizePipe, ButtonModule, TagModule, ShowLocationDialog, ChartModule, GoogleMapsModule],
  templateUrl: './car-details-general-info-component.html',
  styleUrl: './car-details-general-info-component.scss'
})
export class CarDetailsGeneralInfoComponent implements OnInit {

  @Input() carID: string | undefined;
  @Input({ required: true }) carDetails: Car;

  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private carService = inject(CarService);
  private activatedRoute = inject(ActivatedRoute);

  endLocationDialog = false;

  // Some cars come back without telemetry; fall back to an empty object so the template can read fields safely
  get telemetry(): Partial<Telemetry> {
    return this.carDetails?.telemetry ?? {};
  }

  fuelChartData = signal<object | null>(null);
  fuelChartOptions = signal<object>({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { parsed: { y: number } }) => ` ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: '#94a3b8', maxTicksLimit: 6 },
      },
      y: {
        min: 0,
        max: 100,
        grid: { color: '#f1f5f9' },
        ticks: { font: { size: 10 }, color: '#94a3b8', callback: (v: number) => v + '%' },
      },
    },
  });

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) this.loadFuelHistory(id);
  }

  private loadFuelHistory(carId: string): void {
    this.carService.getFuelHistory(carId).subscribe({
      next: (res) => {
        const points = res.data?.points ?? [];
        const labels = points.map(d =>
          new Date(d.timestamp+'Z').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        );
        const values = points.map(d => d.fuelLevel);

        this.fuelChartData.set({
          labels,
          datasets: [{
            data: values,
            fill: true,
            tension: 0.4,
            borderColor: '#2563eb',
            borderWidth: 2,
            backgroundColor: 'rgba(37,99,235,0.08)',
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBackgroundColor: '#2563eb',
          }],
        });
      },
    });
  }

  openEndLocationDialog = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${this.carDetails?.telemetry?.position!.latitude},${this.carDetails?.telemetry?.position!.longitude}`;
    window.open(url, '_blank');
  }

  goToIotDataLogPage = (imei: string) => {
    this.router.navigate(['/iot'], { queryParams: { tab: 0, search: imei }, queryParamsHandling: '' });
  }

  goToManageIOTPage = (imei: string) => {
    this.router.navigate(['/iot'], { queryParams: { tab: 1, search: imei }, queryParamsHandling: '' });
  }

  goToRidePage = (search: string) => {
    const tab = this.carDetails?.model.rentalType === RentalType.CAR_RENTAL ? 'RIDE_RENTAL' : 'RIDE_SHARING';
    this.router.navigate(['/rides'], { queryParams: { search, tab }, queryParamsHandling: '' });
  }

  get ridePageLink(): string {
    const tab = this.carDetails?.model.rentalType === RentalType.CAR_RENTAL ? 'RIDE_RENTAL' : 'RIDE_SHARING';
    const plate = this.carDetails?.plateNo ?? '';
    return `/rides?search=${encodeURIComponent(plate)}&tab=${tab}`;
  }

  onRidesCardClick(event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey) {
      window.open(this.ridePageLink, '_blank');
      return;
    }
    this.goToRidePage(this.carDetails?.plateNo ?? '');
  }

  getBatteryPercentage = (powerSupplyVoltage: number | null | undefined): number => {
    if (!powerSupplyVoltage) return 0;
    return Math.min(Math.max((powerSupplyVoltage / 12) * 100, 0), 100);
  }

  getBatteryColor = (powerSupplyVoltage: number | null | undefined): string => {
    const pct = this.getBatteryPercentage(powerSupplyVoltage);
    if (pct >= 70) return 'var(--dark-green-background)';
    if (pct >= 30) return 'var(--background-progress-bar)';
    return 'var(--status-discharged-text)';
  }

  getFuelColor = (value: number): string => {
    if (value >= 70) return 'var(--dark-green-background)';
    if (value >= 30) return 'var(--background-progress-bar)';
    return 'var(--status-discharged-text)';
  }

  timeAgo = (dateStr: string | undefined): string => {
    if (!dateStr) return '---';
    const diff = Date.now() - new Date(dateStr).getTime();
    const totalMins = Math.floor(diff / 60000);
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins}min`);
    return parts.join(' ') + ' ago';
  }

  onPositionMapReady(map: google.maps.Map) {
    // Draw zones
    GREEN_ZONES.forEach(points => {
      new google.maps.Polygon({
        paths: points, map,
        strokeColor: '#2e7d32', strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: '#4caf50', fillOpacity: 0.18, clickable: false, zIndex: 1,
      });
    });
    RED_ZONES.forEach(points => {
      new google.maps.Polygon({
        paths: points, map,
        strokeColor: '#c62828', strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: '#f44336', fillOpacity: 0.18, clickable: false, zIndex: 1,
      });
    });

    // Car marker — same style as cars-list-map
    const pos = this.carDetails?.telemetry?.position;
    if (!pos?.latitude || !pos?.longitude) return;

    const isSharing = this.carDetails.model?.rentalType === RentalType.CAR_SHARING;
    const pinSrc = isSharing ? 'assets/icons/sharing-location.svg' : 'assets/icons/rental-location.svg';
    const carSrc = isSharing ? 'assets/icons/car-location-sharing.svg' : 'assets/icons/car-location-rental.svg';

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:relative;width:34px;height:52px;';

    const pin = document.createElement('img');
    pin.src = pinSrc;
    pin.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';

    const whiteBg = document.createElement('div');
    whiteBg.style.cssText = 'position:absolute;width:26px;height:26px;top:6px;left:50%;transform:translateX(-50%);background:#fff;border-radius:50%;';

    const carIcon = document.createElement('img');
    carIcon.src = carSrc;
    carIcon.style.cssText = 'position:absolute;width:22px;height:22px;top:7px;left:50%;transform:translateX(-50%);';

    wrapper.appendChild(pin);
    wrapper.appendChild(whiteBg);
    wrapper.appendChild(carIcon);

    new google.maps.marker.AdvancedMarkerElement({
      position: { lat: pos.latitude, lng: pos.longitude },
      map,
      title: `${this.carDetails.model?.maker} ${this.carDetails.model?.name}`,
      content: wrapper,
    });
  }
}
